import { PlugZap, TriangleAlert } from "lucide-react";

import { ErrorConsulta, EsqueletoTabla } from "../componentes/Errores.tsx";
import { InsigniaDe, Seccion, Tarjeta, Vacio, cx } from "../componentes/ui.tsx";
import { usarIntegraciones } from "../ganchos/datos.ts";
import { fechaConHora, haceRato } from "../formato.ts";
import type { Conexion, EstadoConexion } from "../api/tipos.ts";
import { ESTADO_CONEXION } from "../vocabulario.ts";
import { descripcionDe, ordenar, pideAtencion, ultimaLlamada } from "./conexiones-modelo.ts";

/**
 * Qué hay conectado a esta cuenta y cómo va cada cosa (SPEC-026).
 *
 * POR QUÉ ESTA PANTALLA EXISTE: el estado ya se calculaba —en cada corrida, para
 * decidir si mandar una alarma— y se tiraba. El dueño no tenía forma de entrar y
 * comprobar que su ERP sigue conectado: se enteraba de que la integración
 * existía el día que dejaba de funcionar.
 *
 * Y hay una asimetría que la hace necesaria: quien nota que falta un pago es el
 * dueño; quien puede arreglar la integración es otro. Sin una pantalla, el dueño
 * no tiene nada que enseñarle.
 *
 * LO QUE NO HACE: no ofrece ningún botón. Aquí no se revoca, no se reintenta y
 * no se configura nada — eso vive en Configuración, y mezclarlo convertiría una
 * pantalla de consulta en una donde un clic distraído corta el acceso de un ERP
 * en producción.
 */
export function Conexiones() {
  const { data, isPending, error, refetch } = usarIntegraciones();
  const conexiones = data?.conexiones ?? [];

  if (error) return <ErrorConsulta error={error} alReintentar={() => void refetch()} />;

  const ordenadas = ordenar(conexiones);
  const enSilencio = conexiones.filter(pideAtencion);

  return (
    <Seccion
      titulo="Conexiones"
      descripcion="Qué sistemas están conectados a tu cuenta, qué han leído y cuándo lo hicieron."
    >
      {isPending ? (
        <Tarjeta className="p-4">
          <EsqueletoTabla filas={3} />
        </Tarjeta>
      ) : conexiones.length === 0 ? (
        <Tarjeta>
          <Vacio icono={PlugZap} titulo="No hay ningún sistema conectado">
            Tu cartera se lleva desde el panel, que es perfectamente válido y lo que hace la
            mayoría. Si tienes un ERP y quieres conectarlo, se empieza creando una credencial en
            Configuración.
          </Vacio>
        </Tarjeta>
      ) : (
        <>
          {enSilencio.length > 0 && (
            <Tarjeta className="border-riesgo/30 bg-riesgo-suave p-4">
              <div className="flex gap-3">
                <TriangleAlert className="mt-0.5 size-5 shrink-0 text-riesgo" />
                <div className="space-y-1">
                  <p className="font-medium">
                    {enSilencio.length === 1
                      ? "Un sistema dejó de leer y hay pagos esperándolo."
                      : `${enSilencio.length} sistemas dejaron de leer y hay pagos esperándolos.`}
                  </p>
                  {/* Se dice la consecuencia, no el síntoma. "Lleva 72 horas sin
                      leer" no le dice a nadie qué se está rompiendo; "tu ERP no
                      se está enterando de los pagos" sí. */}
                  <p className="text-sm leading-relaxed text-tenue">
                    Mientras esto siga así, esos pagos están conciliados aquí y no han llegado a tu
                    otro sistema. Enséñale esta pantalla a quien mantiene la integración: lo que
                    hay que mirar es si el proceso sigue corriendo en su servidor.
                  </p>
                </div>
              </div>
            </Tarjeta>
          )}

          <ul className="space-y-3">
            {ordenadas.map((c) => (
              <Fila key={c.prefijo} conexion={c} horas={data?.horasDeSilencio ?? 48} />
            ))}
          </ul>

          {data && data.rechazosSinAtribuir > 0 && (
            <p className="max-w-prose text-sm leading-relaxed text-tenue">
              Además hay {data.rechazosSinAtribuir} carga(s) rechazadas que no vinieron por
              ninguna de estas credenciales — se cargaron desde el panel. Están en Cargas
              rechazadas.
            </p>
          )}
        </>
      )}
    </Seccion>
  );
}

function Fila({ conexion: c, horas }: { conexion: Conexion; horas: number }) {
  const apagada = c.estado === "REVOCADA";

  return (
    <li>
      <Tarjeta className={cx("p-4", apagada && "opacity-60")}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              {/* El nombre que le pusieron al crearla es lo que una persona
                  reconoce. El prefijo va detrás porque es lo que sirve para
                  hablar de ella con soporte sin nombrar el secreto. */}
              <span className="font-medium">{c.nombre || "Sin nombre"}</span>
              <code className="text-xs text-tenue">{c.prefijo}</code>
              <EstadoDe estado={c.estado} />
            </div>

            {/* Lo que DICE ser, y se dice que lo dice. Presentarlo como un hecho
                verificado sería mentir: cualquiera puede mandar esa cadena. */}
            {c.agente ? (
              <p className="text-sm text-tenue">
                Se identifica como <code className="text-tinta">{c.agente}</code>
              </p>
            ) : (
              <p className="text-sm text-tenue">
                Todavía no ha dicho qué software es. Lo dirá en su próxima llamada si usa una
                versión reciente del cliente de integración.
              </p>
            )}
          </div>

          <div className="text-right text-sm">
            <p className="text-tenue">{descripcionDe(c, horas)}</p>
            {c.ultimaLectura && (
              <p className="text-xs text-tenue" title={fechaConHora(c.ultimaLectura)}>
                última lectura {haceRato(c.ultimaLectura)}
              </p>
            )}
          </div>
        </div>

        {!apagada && (
          <dl className="mt-4 grid grid-cols-2 gap-4 border-t border-borde pt-3 text-sm sm:grid-cols-4">
            <Dato etiqueta="Leído hasta" valor={c.ultimoEventoLeido || "—"} />
            <Dato
              etiqueta="Pagos por recoger"
              valor={c.sinLeer > 0 ? c.sinLeer : "ninguno"}
              alerta={c.estado === "EN_SILENCIO" && c.sinLeer > 0}
            />
            {/* `ultimoUso` y `ultimaLectura` son distintos a propósito: un ERP
                que solo sube facturas nunca lee eventos, y sin este dato
                parecería muerto cuando está trabajando. */}
            <Dato
              etiqueta="Última llamada"
              valor={ultimaLlamada(c) ? haceRato(ultimaLlamada(c)) : "nunca"}
            />
            <Dato
              etiqueta="Le hemos rechazado"
              valor={c.rechazos > 0 ? `${c.rechazos} fila(s)` : "nada"}
              alerta={c.rechazos > 0}
            />
          </dl>
        )}
      </Tarjeta>
    </li>
  );
}

function Dato({
  etiqueta,
  valor,
  alerta,
}: {
  etiqueta: string;
  valor: string | number;
  alerta?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs text-tenue">{etiqueta}</dt>
      <dd className={cx("font-medium", alerta && "text-riesgo-tinta")}>{valor}</dd>
    </div>
  );
}

function EstadoDe({ estado }: { estado: EstadoConexion }) {
  // Desde el vocabulario, como el resto de estados del panel: el texto y el
  // porqué viajan juntos, así que el tooltip explica sin que haya que
  // escribirlo aquí otra vez.
  return <InsigniaDe etiqueta={ESTADO_CONEXION[estado]} />;
}
